/*
 * RS related products slider (<rs-related-slider>, layout "slider" of
 * sections/rs-related-products.liquid). One row of products, as many visible as the columns
 * set for the screen size. Arrows move by one page (the visible products); with "loop" the last
 * page goes back to the first and the other way round. Dots = pages. Optional autoplay, paused
 * while the mouse or keyboard focus is on the slider, off with "reduce motion".
 * The products arrive later (Dawn's <product-recommendations> fills the section), so the element
 * sets itself up when it is inserted.
 */
if (!customElements.get('rs-related-slider')) {
  customElements.define(
    'rs-related-slider',
    class RsRelatedSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.rs-rp__list');
        if (!this.track) return;
        this.prev = this.querySelector('[data-rs-rp-prev]');
        this.next = this.querySelector('[data-rs-rp-next]');
        this.dotsBox = this.querySelector('[data-rs-rp-dots]');
        this.loop = this.dataset.loop === 'true';

        this.prev?.addEventListener('click', () => this.move(-1));
        this.next?.addEventListener('click', () => this.move(1));
        this.track.addEventListener('scroll', () => this.update(), { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.setup());
        this.resizeObserver.observe(this.track);
        this.setup();

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.dataset.autoplay === 'true' && !reduce) {
          const pause = () => (this.paused = true);
          const resume = () => (this.paused = false);
          this.addEventListener('mouseenter', pause);
          this.addEventListener('mouseleave', resume);
          this.addEventListener('focusin', pause);
          this.addEventListener('focusout', resume);
          this.timer = window.setInterval(() => {
            if (!this.paused && this.isConnected && this.classList.contains('is-scrollable')) this.move(1, true);
          }, (Number(this.dataset.autoplaySpeed) || 5) * 1000);
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.clearInterval(this.timer);
      }

      get pageWidth() {
        return this.track.clientWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      }

      get pages() {
        return Math.max(1, Math.round((this.track.scrollWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0)) / this.pageWidth));
      }

      get page() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= max - 2) return this.pages - 1;
        return Math.round(this.track.scrollLeft / this.pageWidth);
      }

      setup() {
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        this.classList.toggle('is-scrollable', scrollable);
        if (this.dotsBox) {
          this.dotsBox.replaceChildren();
          if (scrollable) {
            for (let i = 0; i < this.pages; i++) {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'rs-rp__dot';
              dot.setAttribute('aria-label', `${i + 1} / ${this.pages}`);
              dot.addEventListener('click', () => this.goTo(i));
              this.dotsBox.append(dot);
            }
          }
        }
        // Arrows in the middle of the product images, not of the whole cards.
        const media = this.track.querySelector('.card__inner, .card__media');
        if (media) this.style.setProperty('--rs-rp-arrow-top', `${media.offsetTop + media.offsetHeight / 2}px`);
        this.update();
      }

      goTo(page) {
        const last = this.pages - 1;
        const target = Math.min(Math.max(page, 0), last);
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollTo({ left: target === last ? max : target * this.pageWidth });
      }

      move(dir, auto) {
        const last = this.pages - 1;
        let target = this.page + dir;
        if (target > last) target = this.loop || auto ? 0 : last;
        if (target < 0) target = this.loop ? last : 0;
        this.goTo(target);
      }

      update() {
        const page = this.page;
        const last = this.pages - 1;
        if (this.prev) this.prev.disabled = !this.loop && page <= 0;
        if (this.next) this.next.disabled = !this.loop && page >= last;
        this.dotsBox?.querySelectorAll('.rs-rp__dot').forEach((dot, i) => dot.setAttribute('aria-current', i === page ? 'true' : 'false'));
      }
    }
  );
}
